import { useEffect } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useAuth } from '@/contexts/AuthContext';
import { supabase } from '@/integrations/supabase/client';
import { useDashboardPermissions } from './useDashboardPermissions';

const safeCount = async (fn: () => Promise<{ count: number | null }>) => {
  try {
    const { count } = await fn();
    return count ?? 0;
  } catch {
    return 0;
  }
};

export interface PendingApprovalsBreakdown {
  listings: number;
  applications: number;
  reviews: number;
  reports: number;
  packageInquiries: number;
}

export function usePendingApprovalsCount() {
  const { user } = useAuth();
  const { permissions: p } = useDashboardPermissions();
  const qc = useQueryClient();

  const queryKey = [
    'hdr-pending-approvals',
    user?.id,
    p.canApproveListings,
    p.canApproveApplications,
    p.canModerateReviews,
    p.canHandleReports,
    p.canManagePackageInquiries,
  ];

  const { data } = useQuery({
    queryKey,
    queryFn: async (): Promise<{ total: number; breakdown: PendingApprovalsBreakdown }> => {
      const breakdown: PendingApprovalsBreakdown = {
        listings: 0, applications: 0, reviews: 0, reports: 0, packageInquiries: 0,
      };
      if (!user) return { total: 0, breakdown };

      if (p.canApproveListings) {
        const [props, plots, projs] = await Promise.all([
          safeCount(() => supabase.from('properties').select('id', { count: 'exact', head: true }).eq('approval_status', 'pending_review') as any),
          safeCount(() => supabase.from('plots').select('id', { count: 'exact', head: true }).eq('approval_status', 'pending_review') as any),
          safeCount(() => supabase.from('projects').select('id', { count: 'exact', head: true }).eq('approval_status', 'pending_review') as any),
        ]);
        breakdown.listings = props + plots + projs;
      }

      if (p.canApproveApplications) {
        const [a, b, c] = await Promise.all([
          safeCount(() => supabase.from('agency_applications').select('id', { count: 'exact', head: true }).eq('status', 'pending') as any),
          safeCount(() => supabase.from('agent_applications').select('id', { count: 'exact', head: true }).eq('status', 'pending') as any),
          safeCount(() => supabase.from('developer_applications').select('id', { count: 'exact', head: true }).eq('status', 'pending') as any),
        ]);
        breakdown.applications = a + b + c;
      }

      if (p.canModerateReviews) {
        breakdown.reviews = await safeCount(() => supabase.from('reviews').select('id', { count: 'exact', head: true }).eq('status', 'pending') as any);
      }

      if (p.canHandleReports) {
        breakdown.reports = await safeCount(() => supabase.from('reports').select('id', { count: 'exact', head: true }).in('status', ['open', 'pending']) as any);
      }

      if (p.canManagePackageInquiries) {
        breakdown.packageInquiries = await safeCount(() => supabase.from('package_inquiries').select('id', { count: 'exact', head: true }).eq('status', 'new') as any);
      }

      const total = breakdown.listings + breakdown.applications + breakdown.reviews + breakdown.reports + breakdown.packageInquiries;
      return { total, breakdown };
    },
    enabled: !!user,
    refetchInterval: 60000,
    retry: false,
  });

  useEffect(() => {
    if (!user) return;
    const tables = [
      p.canApproveListings && 'properties',
      p.canApproveListings && 'plots',
      p.canApproveListings && 'projects',
      p.canApproveApplications && 'agency_applications',
      p.canApproveApplications && 'agent_applications',
      p.canApproveApplications && 'developer_applications',
      p.canModerateReviews && 'reviews',
      p.canHandleReports && 'reports',
      p.canManagePackageInquiries && 'package_inquiries',
    ].filter(Boolean) as string[];

    if (!tables.length) return;

    const channel = supabase.channel(`hdr-approvals-${user.id}`);
    tables.forEach((t) => {
      channel.on('postgres_changes', { event: '*', schema: 'public', table: t }, () => {
        qc.invalidateQueries({ queryKey: ['hdr-pending-approvals'] });
      });
    });
    channel.subscribe();
    return () => { supabase.removeChannel(channel); };
  }, [user?.id, p.canApproveListings, p.canApproveApplications, p.canModerateReviews, p.canHandleReports, p.canManagePackageInquiries, qc]);

  return data ?? { total: 0, breakdown: { listings: 0, applications: 0, reviews: 0, reports: 0, packageInquiries: 0 } };
}
